/* Copyright (c) 2021-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { CapacityBasedFeeTypeEnum } from "~/models/assignmentRule";
import { LogModified } from "./log";

export type Environment = {
  application: { id: string; name: string };
  attachedServiceLevel: {
    serviceLevel: {
      capacityBasedFee: {
        defaultPercentage: string;
        maxPercentage: string;
        minPercentage: string;
      };
      id: string;
      index: number;
      name: string;
    };
    capacityBasedFeeType: CapacityBasedFeeTypeEnum;
    capacityBasedPercentageFee: string;
    effectiveCapacityBasedPercentageFee: string;
  };
  billingRules: {
    incidents: {
      officeHours: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
    };
    serviceRequests: {
      officeHours: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
    };
  };
  customer: { id: string; name: string };
  details: null | string;
  id: string;
  log: LogModified;
  name: string;
  removed: boolean;
  status: string;
  cfRepoPath: string;
  environmentStatus: {
    assignmentRule: { id: string; name: string };
    log: LogModified;
    value: string;
  };
  generalMapping: boolean;
  synchronizedAt: string;
  timeline: {
    softLaunch: null | string;
    goLive: null | string;
    offboarding: null | string;
  };
  type: string;
};

export type EnvironmentRouteParams = {
  environmentId: string;
};

export enum EnvironmentStatus {
  NOT_IN_SCOPE = "NOT_IN_SCOPE",
  ONBOARDING = "ONBOARDING",
  LIVE = "LIVE",
  OFFBOARDING = "OFFBOARDING",
  TERMINATION = "TERMINATION",
}

export enum EnvType {
  PRODUCTION = "PRODUCTION",
  STAGING = "STAGING",
  DEVELOPMENT = "DEVELOPMENT",
  OTHER = "OTHER",
  UNKNOWN = "UNKNOWN",
}

export type EnvironmentDetailsInput = {
  timeline: {
    softLaunch: string | null;
    goLive: string | null;
    offboarding: string | null;
  };
  attachedServiceLevel: {
    capacityBasedFeeType: CapacityBasedFeeTypeEnum;
    serviceLevelId: string;
    capacityBasedPercentageFee: string;
  } | null;
  billingRules: {
    serviceRequests: {
      officeHours: {
        pricePerHour: string;
      };
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
    };
    incidents: {
      officeHours: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursBeforeMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
      outsideOfficeHoursAfterMidnight: {
        defaultPricePerHour: string;
        pricePerHour: string;
      };
    };
  };
  status: string;
  cfRepoPath: string;
  name: string;
};

export type EnvironmentCreateInput = {
  name: string;
  type: string;
  contactPersonId: string;
  description: string;
};

export type DiscoveryRule = {
  id: string;
  match: {
    tags: { key: string; value: string }[];
    type: string[];
    subtype: string[];
    resourceGroup: string[];
    cloudAccount: string[];
    subnetIds: string[];
    securityGroupIds: string[];
    projectId: string[];
  };
};
